import { useEffect, useRef, useState } from "react";

/**
 * Whether the screen has been left alone long enough for its chrome to recede
 * (web-ui ticket 03, extracted in ticket 08).
 *
 * It lived inside `ControlBar` while the bar was the only thing that faded.
 * Ticket 08 puts a top bar on the prompter that has to fade *with* it, and two
 * components each running their own timer off the same three events is two
 * answers to a question with one: they would agree almost always, and the
 * "almost" is a top bar dimming a beat after the bar under it for no reason
 * anybody could see. So the state is one fact, owned by the host that mounts
 * both, and this is the hook it owns it with.
 *
 * The state flips at most twice per idle period rather than on every mouse move:
 * a pointer crossing the screen must not re-render the prompter's chrome sixty
 * times, and that chrome is over a compositor with better things to do.
 */

/** How long the pointer has to be still before the chrome recedes. */
export const IDLE_AFTER_MS = 2_500;

/**
 * What counts as somebody being here.
 *
 * `pointerdown` as well as the move, and it is not redundant: a pointer that
 * arrives from off-screen, or one driven by anything other than a hand, can press
 * a button without ever having moved over the page — and chrome that stayed faded
 * under a press it had just accepted would look broken.
 */
const WAKES = ["pointermove", "pointerdown", "keydown"] as const;

export function useIdleFade(enabled: boolean): boolean {
  const [idle, setIdle] = useState(false);
  const isIdle = useRef(false);

  useEffect(() => {
    if (!enabled) {
      isIdle.current = false;
      setIdle(false);
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const wake = (): void => {
      if (isIdle.current) {
        isIdle.current = false;
        setIdle(false);
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        isIdle.current = true;
        setIdle(true);
      }, IDLE_AFTER_MS);
    };
    wake();
    for (const event of WAKES) window.addEventListener(event, wake);
    return () => {
      clearTimeout(timer);
      for (const event of WAKES) window.removeEventListener(event, wake);
    };
  }, [enabled]);

  return idle;
}
